import { useEffect, useRef, useState } from "react";
import { Copy, CreditCard, Link2, Loader2, Receipt } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import api, { formatApiErrorDetail } from "@/lib/api";
import { loadSquareSdk, SQUARE_TEST_CARD_HINT } from "@/lib/squarePayments";
import { PAYMENTS } from "@/constants/testIds";
import PatientPicker from "@/components/agenda/PatientPicker";

/** Modal único para cobrar con Square: generar un link de cobro (sena o
 * total) o cobrar presencial con tarjeta (Web Payments SDK). Reutilizado
 * desde la ficha de paciente, el detalle de un turno, y la página de Pagos
 * (donde se elige el paciente con el buscador). */
export default function SquareChargeModal({ open, onOpenChange, patient, appointmentId, serviceId, defaultAmount, defaultNote, onCreated }) {
  const [mode, setMode] = useState("link");
  const [selectedPatient, setSelectedPatient] = useState(patient || null);
  const [amount, setAmount] = useState(defaultAmount ? String(defaultAmount) : "");
  const [isDeposit, setIsDeposit] = useState(false);
  const [note, setNote] = useState(defaultNote || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [linkResult, setLinkResult] = useState(null);
  const [chargeResult, setChargeResult] = useState(null);
  const [notConnected, setNotConnected] = useState(false);

  const [cardReady, setCardReady] = useState(false);
  const [cardLoading, setCardLoading] = useState(false);
  const cardContainerRef = useRef(null);
  const cardInstanceRef = useRef(null);

  useEffect(() => {
    if (open) {
      setMode("link");
      setSelectedPatient(patient || null);
      setAmount(defaultAmount ? String(defaultAmount) : "");
      setIsDeposit(false);
      setNote(defaultNote || "");
      setError("");
      setLinkResult(null);
      setChargeResult(null);
      setNotConnected(false);
      setCardReady(false);
    } else {
      cardInstanceRef.current?.destroy?.();
      cardInstanceRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (mode !== "card" || cardInstanceRef.current || !open) return;
    let cancelled = false;
    setCardLoading(true);
    (async () => {
      try {
        const { data: config } = await api.get("/square/public-config");
        const Square = await loadSquareSdk(config.environment);
        if (cancelled) return;
        const payments = Square.payments(config.application_id, config.location_id);
        const card = await payments.card();
        await card.attach(cardContainerRef.current);
        cardInstanceRef.current = card;
        setCardReady(true);
      } catch (err) {
        if (err.response?.status === 400) setNotConnected(true);
        else setError(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo cargar el formulario de tarjeta");
      } finally {
        if (!cancelled) setCardLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [mode, open]);

  const validAmount = Number(amount) > 0;

  const handleGenerateLink = async () => {
    if (!selectedPatient || !validAmount) {
      setError("Selecciona un paciente e ingresa un monto válido");
      return;
    }
    setSaving(true);
    setError("");
    setNotConnected(false);
    try {
      const { data } = await api.post("/payments/square/links", {
        patient_id: selectedPatient.id,
        appointment_id: appointmentId || null,
        service_id: serviceId || null,
        amount: Number(amount),
        is_deposit: isDeposit,
        note: note || null,
      });
      setLinkResult(data);
      onCreated?.(data);
    } catch (err) {
      if (err.response?.status === 400 && (err.response?.data?.detail || "").includes("Square")) setNotConnected(true);
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const handleCharge = async () => {
    if (!selectedPatient || !validAmount) {
      setError("Selecciona un paciente e ingresa un monto válido");
      return;
    }
    if (!cardInstanceRef.current) {
      setError("El formulario de tarjeta todavía no esta listo");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const tokenResult = await cardInstanceRef.current.tokenize();
      if (tokenResult.status !== "OK") {
        setError(tokenResult.errors?.[0]?.message || "No se pudo procesar la tarjeta");
        return;
      }
      const { data } = await api.post("/payments/square/charge", {
        patient_id: selectedPatient.id,
        appointment_id: appointmentId || null,
        amount: Number(amount),
        is_deposit: isDeposit,
        source_id: tokenResult.token,
        note: note || null,
      });
      setChargeResult(data);
      onCreated?.(data);
    } catch (err) {
      if (err.response?.status === 400 && (err.response?.data?.detail || "").includes("Square")) setNotConnected(true);
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const handleCopyLink = async () => {
    if (!linkResult) return;
    await navigator.clipboard.writeText(linkResult.url);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid={PAYMENTS.chargeModal}>
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Cobrar con Square</DialogTitle>
        </DialogHeader>

        {notConnected ? (
          <div className="py-6 text-center space-y-2" data-testid={PAYMENTS.notConnectedNotice}>
            <p className="text-sm text-muted-foreground">La clínica todavía no conecto su cuenta de Square.</p>
            <p className="text-xs text-muted-foreground">Ve a Configuración para conectarla (solo Admin).</p>
          </div>
        ) : linkResult ? (
          <div className="py-4 space-y-4" data-testid={PAYMENTS.chargeModalLinkResultUrl}>
            <p className="text-sm text-foreground">Link de cobro generado {isDeposit ? "(sena)" : ""} por <span className="font-medium">${Number(amount).toFixed(2)}</span>:</p>
            <div className="flex items-center gap-2">
              <Input readOnly value={linkResult.url} className="text-sm" />
              <Button type="button" variant="outline" size="icon" onClick={handleCopyLink} data-testid={PAYMENTS.chargeModalLinkResultCopyButton}>
                <Copy className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">Copialo y enviaselo al paciente. Cuando lo pague, se va a registrar solo en su ficha.</p>
          </div>
        ) : chargeResult ? (
          <div className="py-4 space-y-3 text-center">
            <p className="text-sm text-foreground">Cobro registrado por <span className="font-medium">${Number(amount).toFixed(2)}</span></p>
            {chargeResult.receipt_url && (
              <a href={chargeResult.receipt_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm text-brand underline" data-testid={PAYMENTS.chargeModalChargeSuccessReceiptLink}>
                <Receipt className="w-4 h-4" /> Ver / descargar recibo
              </a>
            )}
          </div>
        ) : (
          <div className="space-y-4 py-1">
            {!patient && (
              <div data-testid={PAYMENTS.chargeModalPatientPicker}>
                <Label className="text-sm text-muted-foreground mb-1 block">Paciente</Label>
                <PatientPicker value={selectedPatient} onSelect={setSelectedPatient} />
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm text-muted-foreground mb-1 block">Monto (USD)</Label>
                <Input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid={PAYMENTS.chargeModalAmountInput} />
              </div>
              <div className="flex items-center gap-2 pt-6">
                <Switch checked={isDeposit} onCheckedChange={setIsDeposit} data-testid={PAYMENTS.chargeModalDepositToggle} />
                <Label className="text-sm text-muted-foreground">Es una sena (pago parcial)</Label>
              </div>
            </div>

            <div>
              <Label className="text-sm text-muted-foreground mb-1 block">Nota (opcional)</Label>
              <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} data-testid={PAYMENTS.chargeModalNoteInput} />
            </div>

            <Tabs value={mode} onValueChange={setMode}>
              <TabsList className="grid grid-cols-2 w-full">
                <TabsTrigger value="link" data-testid={PAYMENTS.chargeModalTabLink}><Link2 className="w-3.5 h-3.5 mr-1.5" /> Link de cobro</TabsTrigger>
                <TabsTrigger value="card" data-testid={PAYMENTS.chargeModalTabCard}><CreditCard className="w-3.5 h-3.5 mr-1.5" /> Cobro presencial</TabsTrigger>
              </TabsList>
              <TabsContent value="link" className="pt-3">
                <p className="text-xs text-muted-foreground">Se genera un link de pago Square que podes copiar y enviarle al paciente por el canal que prefieras.</p>
              </TabsContent>
              <TabsContent value="card" className="pt-3 space-y-2">
                <div ref={cardContainerRef} className="border border-border rounded-lg p-3 min-h-[56px]" data-testid={PAYMENTS.chargeModalCardContainer}>
                  {cardLoading && <p className="text-xs text-muted-foreground flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Cargando formulario de tarjeta...</p>}
                </div>
                <p className="text-xs text-muted-foreground">{SQUARE_TEST_CARD_HINT}</p>
              </TabsContent>
            </Tabs>

            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid={PAYMENTS.chargeModalCancelButton}>
            {linkResult || chargeResult || notConnected ? "Cerrar" : "Cancelar"}
          </Button>
          {!linkResult && !chargeResult && !notConnected && (
            mode === "link" ? (
              <Button onClick={handleGenerateLink} disabled={saving} data-testid={PAYMENTS.chargeModalGenerateLinkButton}>
                {saving ? "Generando..." : "Generar link"}
              </Button>
            ) : (
              <Button onClick={handleCharge} disabled={saving || !cardReady} data-testid={PAYMENTS.chargeModalChargeButton}>
                {saving ? "Cobrando..." : "Cobrar"}
              </Button>
            )
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
